import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useState, useCallback, useRef } from 'react';
import { $getSelection, $isRangeSelection } from 'lexical';
import { LexicalEditor } from 'lexical';
import * as React from 'react';
import { IconButton, Box, Popover, Icon, Button, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';


// Helper function to generate character arrays from Unicode code points
const generateCharsFromRange = (start: number, end: number): string[] => {
  return Array.from({ length: end - start + 1 }, (_, i) => String.fromCharCode(start + i));
};

const generateCharsFromCodePoints = (codePoints: number[]): string[] => {
  return codePoints.map((code) => String.fromCharCode(code));
};

// Generate accented Latin characters dynamically
// Based on KMS screenshot: uppercase (192-214, 216-221), lowercase (224-246, 248-253), special chars, ligatures
const ACCENT_CHARS = [
  ...generateCharsFromRange(192, 214), // À-Ö
  ...generateCharsFromCodePoints([215]), // ×
  ...generateCharsFromRange(216, 221), // Ø-Ý
  ...generateCharsFromCodePoints([222, 223]), // Þ, ß
  ...generateCharsFromRange(224, 246), // à-ö
  ...generateCharsFromRange(248, 253), // ø-ý
  ...generateCharsFromCodePoints([254, 255]), // þ, ÿ
  ...generateCharsFromCodePoints([338, 339, 372, 373, 374, 375, 8482]), // Œ, œ, Ŵ, ŵ, Ŷ, ŷ, ™
];

// Generate punctuation and mathematical symbols dynamically
// Based on KMS screenshot: currency, fractions, operators, arrows
const PUNCTUATION_CHARS = [
  ...generateCharsFromCodePoints([8364]), // €
  ...generateCharsFromCodePoints([45]), // -
  ...generateCharsFromRange(161, 172), // ¡-£
  ...generateCharsFromRange(174, 179), // ®-³ (excluding 180 = ´)
  ...generateCharsFromRange(181, 182), // µ-¿
  ...generateCharsFromRange(184, 191), // µ-¿
  ...generateCharsFromCodePoints([247]), // ÷
  ...generateCharsFromCodePoints([8212]), // — (EM DASH, U+2014)
  ...generateCharsFromCodePoints([8222]), // „ (DOUBLE LOW-9 QUOTATION MARK, U+201E)
  ...generateCharsFromCodePoints([8594]), // ←,
  ...generateCharsFromCodePoints([8658, 8660, 8776, 9670, 9658]), // ⇒ (U+21D2), ⇔ (U+21D4), ≈ (U+2248), ◆ (U+25C6), ► (U+25BA)
  ...generateCharsFromCodePoints([8226]), // • (BULLET, U+2022)
];

function insertSpecialCharacter(editor: LexicalEditor, char: string) {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      selection.insertText(char);
    }
  });
}

const ACCENT = 'accent';
const PUNCTUATION = 'punctuation';


export function SpecialCharacterPickerPlugin(): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState<string>(ACCENT);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  return (
    <Box>
      <IconButton
        size='small'
        onClick={handleOpen}
        className='toolbar-item'
        color={'neutral'}
        title={t('contentEditor.toolbar.specialCharacters.title')}
        aria-label={t('contentEditor.toolbar.specialCharacters.insert')}
        ref={buttonRef}
      >
        <Typography variant='bodyLg' color='neutral.900' sx={{ fontWeight: 500 }} >Ω</Typography>
      </IconButton>
      <Popover
        open={isOpen}
        anchorEl={buttonRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 220, p: 1, boxShadow: 2 } } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '4px', justifyContent: 'center' }}>
          <Button
            key={ACCENT}
            onClick={() => {
              setCategory(ACCENT);
            }}
            className={category === ACCENT ? 'active' : ''}
            type='button'
            onMouseDown={(e) => e.preventDefault()}
            size='small'
            variant={category === ACCENT ? 'contained' : 'outlined'}
          >
            {t('contentEditor.toolbar.specialCharacters.accent')}
          </Button>
          <Button
            key={PUNCTUATION}
            onClick={() => {
              setCategory(PUNCTUATION);
            }}
            type='button'
            onMouseDown={(e) => e.preventDefault()}
            size='small'
            variant={category === PUNCTUATION ? 'contained' : 'outlined'}
          >
            {t('contentEditor.toolbar.specialCharacters.punctuation')}
          </Button>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(30px, 1fr))',
            gap: '4px',
            width: '100%',
            p: 1,
            maxHeight: '325px',
            overflowY: 'auto',
          }}
        >
          {(category === 'accent' ? ACCENT_CHARS : PUNCTUATION_CHARS).map((char, idx) => (
            <button
              key={char + idx}
              onClick={() => {
                insertSpecialCharacter(editor, char);
                handleClose();
              }}
              style={{
                fontSize: '16px',
                padding: '6px',
                cursor: 'pointer',
                border: 'none',
                background: 'transparent',
                borderRadius: 4,
                transition: 'background 0.2s',
              }}
              type='button'
              onMouseDown={(e) => e.preventDefault()}
            >
              {char}
            </button>
          ))}
        </Box>
      </Popover>
    </Box>
  );
}
